import { Input } from "@components/forms/input";
import {
  IRaffleAffiliateDTO,
  IRaffleAffiliateStatusBadgeDTO,
} from "@hooks/dtos/raffles/IRaffleAffiliateDTO";
import {
  RaffleAffiliateStatusEnum,
  RaffleAffiliateStatusTypes,
} from "@hooks/enums/raffle_affiliate";
import { formatName } from "@hooks/functions/formatName";
import { getRaffleAffiliateStatus } from "@hooks/functions/Raffle/status";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

interface IProps {
  raffleKey: string;
  affiliateId: number;
  isOpen: boolean;
  onClose: (updated?: boolean) => void;
}

interface IStatusAction {
  status: RaffleAffiliateStatusTypes;
  label: string;
  variant: string;
  icon?: string;
  confirmTitle: string;
  confirmText: string;
}

interface IButtons {
  proposal: boolean;
  accept: boolean;
  disable: boolean;
  reject: boolean;
  block: boolean;
}

export function ModalRaffleAffiliateEdit({
  raffleKey,
  affiliateId,
  isOpen,
  onClose,
}: IProps) {
  // Stores and states
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const [animate, setAnimate] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const [badgeStatus, setBadgeStatus] =
    useState<IRaffleAffiliateStatusBadgeDTO>();
  const [affiliate, setAffiliate] = useState<IRaffleAffiliateDTO | null>(null);

  const [buttons, setButtons] = useState<IButtons>({
    proposal: false,
    accept: false,
    disable: false,
    reject: false,
    block: false,
  });

  // Status action buttons configuration
  const statusActions: IStatusAction[] = [
    ...(buttons.proposal
      ? [
          {
            status: RaffleAffiliateStatusEnum.OWNER_REQUEST,
            label: "Proposta",
            variant: "info",
            icon: "🤝",
            confirmTitle: "Nova proposta?",
            confirmText: "Deseja enviar uma nova propsota?",
          },
        ]
      : []),

    ...(buttons.accept
      ? [
          {
            status: RaffleAffiliateStatusEnum.ACTIVE,
            label: "Aceitar",
            variant: "success",
            icon: "✓",
            confirmTitle: "Aceitar Afiliação?",
            confirmText:
              "O afiliado será ativado e poderá começar a promover esta Campanha.",
          },
        ]
      : []),

    ...(buttons.disable
      ? [
          {
            status: RaffleAffiliateStatusEnum.INACTIVE,
            label: "Desativar",
            variant: "secondary",
            icon: "○",
            confirmTitle: "Desativar Afiliação?",
            confirmText:
              "O afiliado será desativado e não recebrá mais comissões desta Campanha.",
          },
        ]
      : []),

    ...(buttons.reject
      ? [
          {
            status: RaffleAffiliateStatusEnum.OWNER_REJECTED,
            label: "Rejeitar",
            variant: "warning",
            icon: "✕",
            confirmTitle: "Rejeitar Solicitação?",
            confirmText: "A solicitação de afiliação será rejeitada.",
          },
        ]
      : []),

    ...(buttons.block
      ? [
          {
            status: RaffleAffiliateStatusEnum.OWNER_BLOCK,
            label: "Bloquear",
            variant: "danger",
            icon: "⦸",
            confirmTitle: "Bloquear Afiliado?",
            confirmText:
              "O afiliado será bloqueado e não poderá enviar novas solicitações.",
          },
        ]
      : []),
  ];

  // Fetch affiliate data
  const handleRaffleAffiliate = async () => {
    try {
      setIsLoading(true);
      const params = `?key=${raffleKey}&affiliate=${affiliateId}`;
      const result = await api.get(`/customer/raffle/affiliate${params}`);

      setAffiliate(result.data);
      setBadgeStatus(getRaffleAffiliateStatus(result.data.status));
      setIsLoading(false);
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data.message ??
          "Ocorreu um erro ao carregar os dados!",
        type: "error",
        theme: "dark",
      });
      setIsLoading(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (action: IStatusAction) => {
    if (!affiliate) return;

    // Check if status is already the same
    if (affiliate.status === action.status) {
      pushToastify({
        message: "O status já está definido como " + action.label,
        type: "info",
        theme: "dark",
      });
      return;
    }

    Swal.fire({
      title: action.confirmTitle,
      text: action.confirmText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, continuar!",
      cancelButtonText: "Cancelar",
    }).then(async (result: { isConfirmed: boolean }) => {
      if (result.isConfirmed) {
        try {
          setIsProcessing(true);
          const data = { status: action.status };
          const params = `?key=${raffleKey}&affiliate=${affiliateId}`;
          await api.put(`/customer/raffle/affiliate${params}`, data);

          pushToastify({
            message: "Status atualizado com sucesso!",
            type: "success",
            theme: "dark",
          });

          setUpdated(true);

          // Refresh affiliate data
          await handleRaffleAffiliate();
          setIsProcessing(false);
        } catch (err: any) {
          pushToastify({
            message: err?.response?.data.message ?? "Erro ao atualizar status!",
            type: "error",
            theme: "dark",
          });
          setIsProcessing(false);
        }
      }
    });
  };

  // Handle status change
  const handleNewProposal = async (action: IStatusAction) => {
    if (!affiliate) return;

    // Check if status is already the same
    if (affiliate.status === RaffleAffiliateStatusEnum.ACTIVE) {
      pushToastify({
        message: "Você não pode enviar uma nova proposta para este Afiliado.",
        type: "info",
        theme: "dark",
      });
      return;
    }

    Swal.fire({
      title: action.confirmTitle,
      text: action.confirmText,
      icon: "warning",
      input: "number", // Adds a number input field
      inputLabel: "Percentual de Comissão", // Input field label
      inputPlaceholder: "Digite o percentual", // Input placeholder
      inputAttributes: {
        min: "0", // Minimum value
        max: "100", // Maximum value
        step: "0.1", // Allows decimals
      },
      inputValidator: (value: string) => {
        // Input validation
        const numValue = parseFloat(value);
        if (!value) {
          return "Por favor, informe o percentual!";
        }
        if (numValue < 0 || numValue > 95) {
          return "O percentual deve estar entre 0 e 95!";
        }
      },
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, continuar!",
      cancelButtonText: "Cancelar",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          setIsProcessing(true);

          // Captures the entered percentage
          const percent = parseFloat(result.value); // result.value contains the input value

          const data = {
            affiliate: affiliateId,
            percent,
          };
          const params = `?key=${raffleKey}`;
          await api.post(`/customer/raffle/affiliate${params}`, data);

          pushToastify({
            message: "Proposta enviada com sucesso!",
            type: "success",
            theme: "dark",
          });

          handleRaffleAffiliate();

          setUpdated(true);

          // Refresh affiliate data
          await handleRaffleAffiliate();
          setIsProcessing(false);
        } catch (err: any) {
          pushToastify({
            message: err?.response?.data.message ?? "Erro ao atualizar status!",
            type: "error",
            theme: "dark",
          });
          setIsProcessing(false);
        }
      }
    });
  };

  // Close modal
  function handleCancel() {
    setAnimate(false);
    setTimeout(() => onClose(updated), 300);
  }

  // Handle escape key
  function handleEscape(evt: KeyboardEvent) {
    if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
      handleCancel();
    }
  }

  useEffect(() => {
    // --- Can send new Proposal --- //
    const proposal =
      (affiliate &&
        [
          RaffleAffiliateStatusEnum.INACTIVE,
          RaffleAffiliateStatusEnum.CUSTOMER_REJECTED,
          RaffleAffiliateStatusEnum.OWNER_BLOCK,
        ].includes(affiliate.status as RaffleAffiliateStatusEnum)) ||
      false;

    const accept =
      (affiliate &&
        affiliate.status === RaffleAffiliateStatusEnum.CUSTOMER_REQUEST) ||
      false;

    const reject =
      (affiliate &&
        affiliate.status === RaffleAffiliateStatusEnum.CUSTOMER_REQUEST) ||
      false;

    const disable =
      (affiliate && affiliate.status === RaffleAffiliateStatusEnum.ACTIVE) ||
      false;

    const block =
      (affiliate &&
        [
          RaffleAffiliateStatusEnum.CUSTOMER_REQUEST,
          RaffleAffiliateStatusEnum.INACTIVE,
        ].includes(affiliate.status as RaffleAffiliateStatusEnum)) ||
      false;

    setButtons({
      proposal,
      accept,
      reject,
      disable,
      block,
    });
  }, [affiliate]);

  useEffect(() => {
    if (isOpen) {
      handleRaffleAffiliate();

      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="staticBackdropLabel"
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="contentModallLabel">
                Resumo de Afiliação
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCancel}
              ></button>
            </div>

            <div className="modal-body">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              ) : affiliate ? (
                <>
                  {/* User info header */}
                  {affiliate.customerData && (
                    <div className="text-center mb-4 pb-3 border-bottom">
                      {affiliate.customerData.avatar ? (
                        <img
                          src={affiliate.customerData.avatar}
                          alt={affiliate.customerData.name}
                          className="rounded-circle mb-3"
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          className="rounded-circle bg-primary d-inline-flex align-items-center justify-content-center text-white fw-bold mb-3"
                          style={{
                            width: "80px",
                            height: "80px",
                            fontSize: "32px",
                          }}
                        >
                          {affiliate.customerData.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h4 className="mb-1">
                          {formatName(affiliate.customerData.name || "")}
                        </h4>
                        <Link
                          className="py-1"
                          to={`/profile?atsign=${affiliate.customerData.atSign}`}
                        >
                          <span className="fs-6 badge bg-secondary text-white">
                            @{affiliate.customerData.atSign}
                          </span>
                        </Link>
                        <br className="mb-2" />
                        <span
                          className={`fs-6 badge bg-${badgeStatus?.color}`}
                          title={badgeStatus?.text}
                        >
                          {badgeStatus?.title}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Commission percentage - Professional display */}
                  <div className="mb-4">
                    <div className="card border-primary">
                      <div className="card-body text-center py-4">
                        <small className="text-muted text-uppercase d-block mb-2">
                          Percentual de Comissão
                        </small>
                        <div className="display-6 fw-bold text-primary mb-2">
                          {affiliate.percent}%
                        </div>
                        <small className="text-muted">
                          Comissão sobre cada ticket vendido
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Status action buttons */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold d-block mb-3">
                      Ações de Status
                    </label>
                    <div className="d-flex flex-wrap gap-2 justify-content-center">
                      {statusActions.map((action) => (
                        <button
                          key={action.status}
                          type="button"
                          className={`btn btn-${action.variant} rounded flex-grow-1 flex-sm-grow-0 text-white`}
                          onClick={() => {
                            action.status ===
                            RaffleAffiliateStatusEnum.OWNER_REQUEST
                              ? handleNewProposal(action)
                              : handleStatusChange(action);
                          }}
                          disabled={
                            isProcessing || affiliate.status === action.status
                          }
                          style={{ minWidth: "120px" }}
                        >
                          {action.icon && (
                            <span className="me-1 fs-6">{action.icon}</span>
                          )}
                          {action.label}
                        </button>
                      ))}
                    </div>
                    <small className="d-block text-center text-muted mt-2">
                      {statusActions.length > 0
                        ? "Escolha uma ação para alterar o status da afiliação"
                        : "Nenhuma ação disponível"}
                    </small>
                  </div>

                  {/* Timestamps */}
                  <div className="row mt-4 pt-3 border-top">
                    <div className="col-12 col-sm-6 mb-2 mb-sm-0">
                      <small className="text-muted d-block">Criado em</small>
                      <small className="fw-semibold">
                        {dayjs(affiliate.createdAt).format(
                          "DD/MM/YYYY [às] HH:mm:ss",
                        )}
                      </small>
                    </div>
                    <div className="col-12 col-sm-6 text-sm-end">
                      <small className="text-muted d-block">
                        Atualizado em
                      </small>
                      <small className="fw-semibold">
                        {dayjs(affiliate.updatedAt).format(
                          "DD/MM/YYYY [às] HH:mm:ss",
                        )}
                      </small>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">Nenhum dado encontrado</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={isProcessing}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      ></div>
    </>
  );
}
